import { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { saveAs } from "file-saver";

function Dashboard() {
  // Inventory stats states
  const [stats, setStats] = useState({
    totalItems: 0,
    stockInToday: 0,
    stockOutToday: 0,
  });
  const [stockIns, setStockIns] = useState([]);
  const [stockOuts, setStockOuts] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [stockForm, setStockForm] = useState({
    startDate: "",
    endDate: "",
    branch: "All",
  });
  const [activeTab, setActiveTab] = useState("stockIns");

  // Category states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({ name: "" });
  const [editMode, setEditMode] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);

  // Refs for table containers
  const stockInTableRef = useRef(null);
  const stockOutTableRef = useRef(null);

  // Scroll table functions
  const scrollTable = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 200; // Adjust scroll distance as needed
      ref.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    try {
      setLoadingStats(true);
      const [totalItemsResponse, stockInResponse, stockOutResponse] = await Promise.all([
        axios.get("https://backend.lehuluet.com/api/items/total"),
        axios.get("https://backend.lehuluet.com/api/stockins/stockins/today"),
        axios.get("https://backend.lehuluet.com/api/stockouts/stockouts/today"),
      ]);

      setStats({
        totalItems: totalItemsResponse.data.count,
        stockInToday: stockInResponse.data.count,
        stockOutToday: stockOutResponse.data.count,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Error fetching dashboard stats");
    } finally {
      setLoadingStats(false);
    }
  };

  // Fetch stock ins/outs and filter by date and branch on the frontend
  const fetchStockData = async () => {
    if (!stockForm.startDate || !stockForm.endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    // Validate date range
    const start = new Date(stockForm.startDate);
    const end = new Date(stockForm.endDate + 'T23:59:59.999Z');
    if (end < start) {
      toast.error("End date must be after start date");
      return;
    }

    try {
      setLoadingActivity(true);
      const params = {
        ...(stockForm.branch !== "All" && { fromBranch: stockForm.branch }), // Use fromBranch for backend
      };

      console.log("Fetching stock data with params:", params); // Debugging

      const [stockInResponse, stockOutResponse] = await Promise.all([
        axios.get("https://backend.lehuluet.com/api/stockins/stockins", { params }),
        axios.get("https://backend.lehuluet.com/api/stockouts/stockouts", { params }),
      ]);

      console.log("Stock Ins response:", stockInResponse.data); // Debugging
      console.log("Stock Outs response:", stockOutResponse.data); // Debugging

      // Filter stock data by date and branch on the frontend
      const filteredStockIns = (stockInResponse.data.data || []).filter(stock => {
        const createdAt = new Date(stock.created_at);
        const branchMatch = stockForm.branch === "All" || stock.fromBranch === stockForm.branch;
        return createdAt >= start && createdAt <= end && branchMatch;
      });

      const filteredStockOuts = (stockOutResponse.data.data || []).filter(stock => {
        const createdAt = new Date(stock.created_at);
        const branchMatch = stockForm.branch === "All" || stock.fromBranch === stockForm.branch;
        return createdAt >= start && createdAt <= end && branchMatch;
      });

      console.log("Filtered Stock Ins:", filteredStockIns); // Debugging
      console.log("Filtered Stock Outs:", filteredStockOuts); // Debugging

      setStockIns(filteredStockIns);
      setStockOuts(filteredStockOuts);

      // Set active tab based on available data
      if (filteredStockIns.length > 0 && filteredStockOuts.length === 0) {
        setActiveTab("stockIns");
      } else if (filteredStockOuts.length > 0 && filteredStockIns.length === 0) {
        setActiveTab("stockOuts");
      }
    } catch (error) {
      console.error("Error fetching stock data:", error); // Debugging
      toast.error(error.response?.data?.message || "Error fetching stock data");
    } finally {
      setLoadingActivity(false);
    }
  };

  // Export both stock ins and stock outs to a single CSV
  const exportToCSV = () => {
    const headersStockIn = [
      "Item Name",
      "Category",
      "Brand",
      "Size",
      "Quantity",
      "Unit Price",
      "User",
      "Branch",
      "Date",
      "Note",
    ];

    const headersStockOut = [
      "Item Name",
      "Category",
      "Brand",
      "Size",
      "Quantity",
      "Unit Price",
      "User",
      "For Who",
      "From",
      "For What",
      "Branch",
      "Date",
      "Note",
    ];

    const csvRows = [];

    // Add Stock Ins section
    if (stockIns.length > 0) {
      csvRows.push("Stock Ins");
      csvRows.push(headersStockIn.join(","));
      stockIns.forEach((item) => {
        const row = [
          `"${item.item?.name || "N/A"}"`,
          `"${item.item?.category?.name || "N/A"}"`,
          `"${item.item?.brand || "N/A"}"`,
          `"${item.item?.size || "N/A"}"`,
          item.quantity || "0",
          item.unitprice || "0",
          `"${item.user?.name || "N/A"}"`,
          `"${item.fromBranch || "N/A"}"`,
          `"${new Date(item.created_at).toLocaleDateString()}"`,
          `"${item.note || ""}"`,
        ];
        csvRows.push(row.join(","));
      });
      csvRows.push(""); // Blank line for separation
    }

    // Add Stock Outs section
    if (stockOuts.length > 0) {
      csvRows.push("Stock Outs");
      csvRows.push(headersStockOut.join(","));
      stockOuts.forEach((item) => {
        const row = [
          `"${item.item?.name || "N/A"}"`,
          `"${item.item?.category?.name || "N/A"}"`,
          `"${item.item?.brand || "N/A"}"`,
          `"${item.item?.size || "N/A"}"`,
          item.quantity || "0",
          item.unitprice || "0",
          `"${item.user?.name || "N/A"}"`,
          `"${item.for_who || "N/A"}"`,
          `"${item.from || "N/A"}"`,
          `"${item.for_what || "N/A"}"`,
          `"${item.fromBranch || "N/A"}"`,
          `"${new Date(item.created_at).toLocaleDateString()}"`,
          `"${item.note || ""}"`,
        ];
        csvRows.push(row.join(","));
      });
    }

    // If no data, add a message
    if (stockIns.length === 0 && stockOuts.length === 0) {
      csvRows.push("No records found for the selected date range and branch.");
    }

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `stock_report_${stockForm.startDate}_to_${stockForm.endDate}.csv`);
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get("https://backend.lehuluet.com/api/categories/categories");
      setCategories(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error fetching categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Search categories
  const searchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(
        `https://backend.lehuluet.com/api/categories/categories/search?query=${searchQuery}`
      );
      setCategories(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error searching categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Handle form input changes for categories
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle stock form input changes
  const handleStockFormChange = (e) => {
    const { name, value } = e.target;
    setStockForm({ ...stockForm, [name]: value });
  };

  // Handle category form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      if (editMode) {
        await axios.put(
          `https://backend.lehuluet.com/api/categories/categories/${currentCategoryId}`,
          formData,
          config
        );
        toast.success("Category updated successfully");
      } else {
        await axios.post(
          "https://backend.lehuluet.com/api/categories/categories",
          formData,
          config
        );
        toast.success("Category created successfully");
      }
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving category");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset category form
  const resetForm = () => {
    setFormData({ name: "" });
    setEditMode(false);
    setCurrentCategoryId(null);
  };

  // Edit category
  const handleEdit = (category) => {
    setFormData({ name: category.name });
    setEditMode(true);
    setCurrentCategoryId(category._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Delete category
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      setIsDeleting(id);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found. Please log in again.");
        }

        await axios.delete(`https://backend.lehuluet.com/api/categories/categories/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        toast.success("Category deleted successfully");
        fetchCategories();
      } catch (error) {
        toast.error(error.response?.data?.message || "Error deleting category");
      } finally {
        setIsDeleting(null);
      }
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchCategories();
      } else {
        fetchCategories();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="flex flex-col lg:flex-row justify-between items-center lg:items-start">
        <div className="text-center lg:text-left mb-4 lg:mb-0">
          <h1 className="text-3xl font-semibold text-teal-800">Dashboard</h1>
          <p className="text-gray-600 mt-2 capitalize">
            Overview of your inventory statistics
          </p>
        </div>
        <div className="mt-4 lg:mt-0">
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-teal-500 text-white px-4 py-2 rounded-md shadow-md hover:bg-teal-600 transition-colors"
          >
            Manage Categories
          </button>
        </div>
      </div>

      {/* Stock Filter Form */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Filter Stock Records</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            fetchStockData();
          }}
          className="grid grid-cols-1 sm:grid-cols-4 gap-4"
        >
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={stockForm.startDate}
              onChange={handleStockFormChange}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
              required
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={stockForm.endDate}
              onChange={handleStockFormChange}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
              required
            />
          </div>
      
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loadingActivity}
              className={`w-full px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors ${
                loadingActivity ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loadingActivity ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Fetching...
                </span>
              ) : (
                "Fetch Stock Records"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Inventory Stats */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {loadingStats ? (
          <div className="col-span-3 flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-600"></div>
          </div>
        ) : (
          <>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <h3 className="text-xl font-semibold text-gray-800">Total Items</h3>
              <p className="text-3xl font-bold text-teal-500 mt-2">{stats.totalItems}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <h3 className="text-xl font-semibold text-gray-800">Today's Stock In</h3>
              <p className="text-3xl font-bold text-green-500 mt-2">{stats.stockInToday}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <h3 className="text-xl font-semibold text-gray-800">Today's Stock Out</h3>
              <p className="text-3xl font-bold text-purple-500 mt-2">{stats.stockOutToday}</p>
            </div>
          </>
        )}
      </div>

      {/* Tabbed Stock Records */}
      {loadingActivity ? (
        <div className="mt-8 flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-600"></div>
        </div>
      ) : stockIns.length === 0 && stockOuts.length === 0 ? (
        <div className="mt-8 bg-white p-4 rounded-lg shadow-md text-center">
          <p className="text-gray-600">No records found for the selected date range and branch.</p>
        </div>
      ) : (
        <div className="mt-8 bg-white p-4 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4">
            <div className="flex border-b border-gray-200">
              {stockIns.length > 0 && (
                <button
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === "stockIns"
                      ? "border-b-2 border-teal-500 text-teal-600"
                      : "text-gray-500 hover:text-teal-600"
                  }`}
                  onClick={() => setActiveTab("stockIns")}
                >
                  Stock Ins
                </button>
              )}
              {stockOuts.length > 0 && (
                <button
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === "stockOuts"
                      ? "border-b-2 border-teal-500 text-teal-600"
                      : "text-gray-500 hover:text-teal-600"
                  }`}
                  onClick={() => setActiveTab("stockOuts")}
                >
                  Stock Outs
                </button>
              )}
            </div>
            <button
              onClick={exportToCSV}
              className="bg-teal-500 text-white px-4 py-2 rounded-md hover:bg-teal-600"
            >
              Export All to CSV
            </button>
          </div>
          <div className="mt-4">
            {activeTab === "stockIns" && stockIns.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Stock Ins</h3>
                <div className="relative">
                  <div className="flex justify-between mb-2">
                    <button
                      onClick={() => scrollTable(stockInTableRef, "left")}
                      className="bg-teal-500 text-white p-2 rounded-full hover:bg-teal-600 transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => scrollTable(stockInTableRef, "right")}
                      className="bg-teal-500 text-white p-2 rounded-full hover:bg-teal-600 transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="overflow-x-auto" ref={stockInTableRef}>
                    <table className="w-full table-auto min-w-[800px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Item Name</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Category</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Brand</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Size</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Quantity</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Unit Price</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">User</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Branch</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Date</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockIns.map((stock, index) => (
                          <tr key={index} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.item?.name || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.item?.category?.name || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.item?.brand || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.item?.size || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.quantity}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.unitprice}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.user?.name || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.fromBranch || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                              {new Date(stock.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2 text-gray-700">{stock.note || "N/A"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {activeTab === "stockOuts" && stockOuts.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Stock Outs</h3>
                <div className="relative">
                  <div className="flex justify-between mb-2">
                    <button
                      onClick={() => scrollTable(stockOutTableRef, "left")}
                      className="bg-teal-500 text-white p-2 rounded-full hover:bg-teal-600 transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => scrollTable(stockOutTableRef, "right")}
                      className="bg-teal-500 text-white p-2 rounded-full hover:bg-teal-600 transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="overflow-x-auto" ref={stockOutTableRef}>
                    <table className="w-full table-auto min-w-[1000px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Item Name</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Category</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Brand</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Size</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Quantity</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Unit Price</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">User</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">For Who</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">From</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">For What</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Branch</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Date</th>
                          <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockOuts.map((stock, index) => (
                          <tr key={index} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.item?.name || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.item?.category?.name || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.item?.brand || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.item?.size || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.quantity}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.unitprice}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.user?.name || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.for_who || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.from || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.for_what || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{stock.fromBranch || "N/A"}</td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                              {new Date(stock.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2 text-gray-700">{stock.note || "N/A"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 sm:p-8">
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="text-2xl font-bold text-gray-800">Categories</h3>
                <button
                  className="text-gray-400 hover:text-gray-600 text-xl font-medium transition-colors"
                  onClick={() => setIsModalOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    {editMode ? "Edit Category" : "Add New Category"}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Category Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full sm:w-auto px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-all ${
                          isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center justify-center">
                            <svg
                              className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12 a8 8 0 018-8 m0 0C5.373 0 0 5.373 0 12h4zm2 5.291 A7.962 7.962 0 014 12 H0 c0 3.0 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Loading...
                          </div>
                        ) : editMode ? (
                          "Update Category"
                        ) : (
                          "Add Category"
                        )}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={resetForm}
                          className="w-full sm:w-auto px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="mb-6">
                    <label
                      htmlFor="search"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Search Categories
                    </label>
                    <input
                      type="text"
                      id="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Type to search..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                  {loadingCategories ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-600"></div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-gray-800">
                          Categories
                        </h3>
                        <span className="text-sm text-gray-600 bg-gray-200 px-2 py-1 rounded">
                          {categories.length} {categories.length === 1 ? "category" : "categories"}
                        </span>
                      </div>
                      {categories.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 bg-white rounded-md">
                          No categories found. Create one to get started.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {categories.map((category) => (
                                <tr
                                  key={category._id}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="px-4 py-3 text-sm text-gray-900">{category.name}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <div className="flex gap-3">
                                      <button
                                        onClick={() => handleEdit(category)}
                                        className="text-teal-600 hover:text-teal-800 font-medium transition-colors"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDelete(category._id)}
                                        disabled={isDeleting === category._id}
                                        className={`flex items-center text-red-600 hover:text-red-800 font-medium transition-colors ${
                                          isDeleting === category._id ? "opacity-70 cursor-not-allowed" : ""
                                        }`}
                                      >
                                        {isDeleting === category._id && (
                                          <svg
                                            className="animate-spin mr-2 h-4 w-4 text-red-600"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <circle
                                              className="opacity-25"
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              stroke="currentColor"
                                              strokeWidth="4"
                                            />
                                            <path
                                              className="opacity-75"
                                              fill="currentColor"
                                              d="M4 12 a8 8 0 018-8 m0 0C5.373 0 0 5.373 0 12h4zm2 5.291 A7.962 7.962 0 014 12 H0 c0 3.0 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                          </svg>
                                        )}
                                        {isDeleting === category._id
                                          ? "Deleting..."
                                          : "Delete"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="mt-6 pt-4 border-t flex justify-end">
                <button
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:ring-2 transition-all"
                  onClick={() => setIsModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={true} />
    </div>
  );
}

export default Dashboard;